// src/services/session-logger.service.js

import {
    appendSessionEntries,
    deleteAllSessions,
    deleteSession,
    readAllSessions,
    readSession,
    stashPendingEntries,
} from '@/services/session-logs-db.service';

export const CLIENT_ERROR_TYPE = 'client:error';

const DEFAULT_MAX_SESSIONS = 3;
const DEFAULT_MAX_ENTRIES = 20000;

const FLUSH_INTERVAL_MS = 1000;

const MAX_TEXT_LENGTH = 500;
const MAX_GENERIC_STRING_LENGTH = 1000;

let entryCounter = 0;

/**
 * Записи одной миллисекунды сортируются в базе по id, поэтому счётчик дополнен нулями:
 * так порядок в пределах вкладки совпадает с порядком вызовов log().
 */
function createEntryId(now) {
    entryCounter += 1;

    return `${now}-${String(entryCounter).padStart(6, '0')}-${Math.random().toString(16).slice(2)}`;
}

function trimText(text, maxLength = MAX_TEXT_LENGTH) {
    if (typeof text !== 'string') return text;

    if (text.length <= maxLength) return text;

    return `${text.slice(0, maxLength)}…[trimmed ${text.length - maxLength}]`;
}

function safeClone(value) {
    try {
        return JSON.parse(JSON.stringify(value));
    } catch {
        return '[unserializable]';
    }
}

function sanitizeError(error) {
    if (!error) return null;

    return {
        message: error.message,
        name: error.name,
        type: error.type,
        description: error.description,
        data: safeClone(error.data),
        cause: error.cause
            ? {
                message: error.cause.message,
                name: error.cause.name,
            }
            : undefined,
    };
}

function sanitizeGenericPayload(payload) {
    const cloned = safeClone(payload);

    return trimLargeStrings(cloned);
}

function trimLargeStrings(value) {
    if (typeof value === 'string') {
        return trimText(value, MAX_GENERIC_STRING_LENGTH);
    }

    if (Array.isArray(value)) {
        return value.map(item => trimLargeStrings(item));
    }

    if (value && typeof value === 'object') {
        return Object.keys(value).reduce((acc, key) => {
            const lowerKey = key.toLowerCase();

            if (
                lowerKey.includes('audio') ||
                lowerKey.includes('buffer') ||
                lowerKey.includes('blob') ||
                lowerKey.includes('base64')
            ) {
                acc[key] = '[removed-large-binary-like-field]';
            } else {
                acc[key] = trimLargeStrings(value[key]);
            }

            return acc;
        }, {});
    }

    return value;
}

function sanitizePayload(type, event, payload) {
    // Клиентские ошибки уже очищены при перехвате, а стеку нужен лимит больше общего.
    if (type === CLIENT_ERROR_TYPE) {
        return payload;
    }

    if (event === 'tts') {
        const data = Array.isArray(payload) ? payload[0] : payload;

        return {
            message_id: data?.message_id,
            hasAudio: Boolean(data?.audio),
            audioSize: data?.audio?.byteLength || data?.audio?.length || null,
        };
    }

    if (event === 'message') {
        const data = Array.isArray(payload) ? payload[0] : payload;

        return {
            message_id: data?.message_id,
            is_final: data?.is_final,
            original: {
                text: trimText(data?.original?.text),
            },
            concat: trimText(data?.concat),
            sender: data?.sender
                ? {
                    language: data.sender.language,
                    name: data.sender.name,
                    sender_id: data.sender.sender_id,
                }
                : null,
            translations: data?.translations
                ? Object.keys(data.translations)
                : undefined,
            start_time: data?.start_time,
            end_time: data?.end_time,
            asr_segment: data?.asr_segment
                ? trimLargeStrings(data.asr_segment)
                : undefined,
        };
    }

    if (event === 'connect_error') {
        const error = Array.isArray(payload) ? payload[0] : payload;
        return sanitizeError(error);
    }

    return sanitizeGenericPayload(payload);
}

export function createSessionLogger({
                                        roomCode,
                                        userId,
                                        maxSessions = DEFAULT_MAX_SESSIONS,
                                        maxEntries = DEFAULT_MAX_ENTRIES,
                                    }) {
    const normalizedRoomCode = roomCode || 'unknown-room';

    let pendingEntries = [];
    let flushTimer = null;
    let flushChain = Promise.resolve();

    function buildBatch(entries) {
        return {
            roomCode: normalizedRoomCode,
            userId,
            entries,
            maxSessions,
            maxEntries,
        };
    }

    function flush() {
        if (flushTimer) {
            clearTimeout(flushTimer);
            flushTimer = null;
        }

        if (!pendingEntries.length) return flushChain;

        const batch = pendingEntries;
        pendingEntries = [];

        flushChain = flushChain
            .then(() => appendSessionEntries(buildBatch(batch)))
            .catch(error => {
                // Возвращаем пакет в буфер: его заберёт следующий сброс. Лимит не даёт буферу
                // расти без конца, если база недоступна постоянно (например, запрещена в браузере).
                console.warn('[SessionLogger] IndexedDB write failed', error);
                pendingEntries = [...batch, ...pendingEntries].slice(-maxEntries);
            });

        return flushChain;
    }

    function handlePageHide() {
        if (pendingEntries.length) {
            stashPendingEntries(buildBatch(pendingEntries));
        }

        flush();
    }

    function handleVisibilityChange() {
        // Скрытую вкладку мобильный браузер может выгрузить без pagehide.
        if (document.visibilityState === 'hidden') {
            flush();
        }
    }

    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    function log(type, event, payload = null, meta = null, timestamp = Date.now()) {
        pendingEntries.push({
            id: createEntryId(timestamp),
            timestamp: new Date(timestamp).toISOString(),
            type,
            event,
            payload: sanitizePayload(type, event, payload),
            meta: sanitizeGenericPayload(meta),
        });

        if (!flushTimer) {
            flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
        }
    }

    async function getSessions() {
        await flush();

        return readAllSessions();
    }

    async function getCurrentSession() {
        await flush();

        return readSession(normalizedRoomCode);
    }

    async function exportSession() {
        const session = await getCurrentSession();

        if (!session) {
            console.warn(`[SessionLogger] No logs found for roomCode "${normalizedRoomCode}"`);
            return false;
        }

        const blob = new Blob([JSON.stringify(session, null, 2)], {
            type: 'application/json',
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = `session-${normalizedRoomCode}-logs.json`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        return true;
    }

    async function clearSession() {
        pendingEntries = [];
        await flush();

        return deleteSession(normalizedRoomCode);
    }

    async function clearAllSessions() {
        pendingEntries = [];
        await flush();

        return deleteAllSessions();
    }

    function dispose() {
        window.removeEventListener('pagehide', handlePageHide);
        document.removeEventListener('visibilitychange', handleVisibilityChange);

        return flush();
    }

    return {
        log,
        flush,
        dispose,
        exportSession,
        clearSession,
        clearAllSessions,
        getSessions,
        getCurrentSession,
    };
}
